import React, { useCallback, useEffect } from 'react';
import { Redirect, Stack, useRouter } from 'expo-router';
import { Platform, StyleSheet, View } from 'react-native';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Logo from '@/components/Icons/Logo';
import { getPublicRedirectTarget } from '@/lib/publicRedirects';

// Create styles outside component
const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  contentContainer: {
    width: '100%',
    maxWidth: 540,
    minWidth: 320,
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  logo: {
    width: 40,
    height: 40,
    marginBottom: 20,
  },
  messageContainer: {
    marginBottom: 26,
    alignItems: 'center',
  },
});

// Memoized message component
const NotFoundMessage = React.memo(({ t }) => (
  <View style={styles.messageContainer}>
    <Text color='secondary' size='lg' style={{ lineHeight: 24 }}>
      In the vast expanse of digital space,
    </Text>
    <Text color='secondary' size='lg' style={{ lineHeight: 24 }}>
      we sometimes find ourselves searching
    </Text>
    <Text color='secondary' size='lg' style={{ lineHeight: 24 }}>
      for pages that exist only in memory.
    </Text>
  </View>
));

function NotFoundRoute() {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const router = useRouter();

  const legacyRedirectTarget = Platform.OS === 'web' && typeof window !== 'undefined'
    ? getPublicRedirectTarget(window.location.pathname)
    : null;

  useEffect(() => {
    if (!legacyRedirectTarget || typeof window === 'undefined') return;
    window.location.replace(legacyRedirectTarget);
  }, [legacyRedirectTarget]);

  if (legacyRedirectTarget) {
    return <Redirect href={legacyRedirectTarget} />;
  }

  // Memoize handlers
  const handleHomePress = useCallback(() => {
    router.replace('/');
  }, [router]);

  return (
    <>
      <Stack.Screen options={{ 
        title: '404 | Not Found',
        headerShown: false,
      }} />
      
      <View style={styles.container}>
        <View style={styles.contentContainer}>
          <Logo 
            width={styles.logo.width}
            height={styles.logo.height}
            color={colors.text_color_primary}
            style={styles.logo}
            resizeMode="contain"
            accessibilityLabel={t('common.appLogo')}
          />
          
          <Text size='xxl' weight='bold' color='primary' style={{ marginVertical: 15 }}>
            404 Not Found
          </Text>

          <NotFoundMessage t={t} />

          <Button
            text="Go Home"
            variant="secondary"
            size="medium"
            onPress={handleHomePress}
          />
        </View>
      </View>
    </>
  );
};

export default React.memo(NotFoundRoute);